import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Award, Cog, ShieldCheck, TrendingUp } from "lucide-react";
import HeroDevices from "../components/DeviceMockups";
import { EASE } from "../components/Reveal";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const BENEFITS = [
  { icon: TrendingUp, title: "ATTRACT MORE CLIENTS", testid: "benefit-attract-clients" },
  { icon: ShieldCheck, title: "BUILD CREDIBILITY", testid: "benefit-build-credibility" },
  { icon: Award, title: "SHOWCASE YOUR EXPERTISE", testid: "benefit-showcase-expertise" },
  { icon: Cog, title: "AUTOMATE YOUR BUSINESS", testid: "benefit-automate-business" },
];

export default function Hero({ introDelay = 0 }) {
  const { scrollY } = useScroll();
  const yDevices = useTransform(scrollY, [0, 700], [0, -56]);
  const yGlow = useTransform(scrollY, [0, 700], [0, 90]);
  const d = introDelay;

  return (
    <section data-testid="hero-section" className="relative overflow-hidden">
      <div className="bg-blueprint absolute inset-0 opacity-60" aria-hidden="true" />
      <motion.div
        aria-hidden="true"
        style={{
          y: yGlow,
          background:
            "radial-gradient(closest-side, rgba(212,175,55,0.10), transparent), radial-gradient(closest-side, rgba(37,99,235,0.12), transparent)",
          backgroundPosition: "15% 20%, 85% 70%",
          backgroundRepeat: "no-repeat",
          backgroundSize: "55% 55%, 50% 50%",
        }}
        className="absolute inset-0"
      />
      <div className="relative mx-auto max-w-7xl px-5 pb-16 pt-36 sm:px-8 lg:pb-14 lg:pt-44">
        <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-6">
            <motion.p
              data-testid="hero-eyebrow"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: d + 0.15, ease: EASE }}
              className="font-mono text-[10px] font-semibold leading-loose tracking-[0.18em] text-gold sm:text-xs sm:tracking-[0.3em]"
            >
              STRATEGY&nbsp;&nbsp;•&nbsp;&nbsp;STORY&nbsp;&nbsp;•&nbsp;&nbsp;DESIGN&nbsp;&nbsp;•&nbsp;&nbsp;TECHNOLOGY
            </motion.p>
            <h1 className="mt-7 font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-[3.8rem]">
              <MaskedLine delay={d + 0.3}>
                Your business has <span className="text-gold-gradient">a story.</span>
              </MaskedLine>
              <MaskedLine delay={d + 0.45}>We turn it into</MaskedLine>
              <MaskedLine delay={d + 0.6}>
                <span className="text-gold-gradient">an experience.</span>
              </MaskedLine>
            </h1>
            <motion.p
              data-testid="hero-subheadline"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: d + 0.85, ease: EASE }}
              className="mt-7 max-w-xl text-lg leading-relaxed text-slate-400"
            >
              AT Digital helps businesses uncover what makes them valuable, communicate it clearly, and turn that
              story into a digital experience customers can understand, trust and act on.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: d + 1, ease: EASE }}
            >
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link
                  to="/start"
                  data-testid="hero-start-website-cta"
                  className="group inline-flex items-center gap-2.5 rounded-full bg-gold px-8 py-4 text-sm font-bold text-ink shadow-lg shadow-gold/20 transition-colors duration-300 hover:bg-goldlight"
                >
                  Tell Us Your Story
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                </Link>
                <Link
                  to="/work"
                  data-testid="hero-see-work-cta"
                  className="inline-flex items-center gap-2.5 rounded-full border border-navyline px-8 py-4 text-sm font-semibold text-slate-200 transition-colors duration-300 hover:border-gold/60 hover:text-gold"
                >
                  See Our Work
                </Link>
              </div>
              <p data-testid="hero-capabilities" className="mt-6 font-mono text-[11px] tracking-[0.18em] text-slate-500">
                WEB DESIGN&nbsp;&nbsp;•&nbsp;&nbsp;DEVELOPMENT&nbsp;&nbsp;•&nbsp;&nbsp;AUTOMATION
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.9, delay: d + 1.2 }}
              className="mt-14 grid grid-cols-2 gap-x-6 gap-y-8 border-t border-navyline/70 pt-9 xl:grid-cols-4"
            >
              {BENEFITS.map((b) => (
                <div key={b.title} data-testid={b.testid} className="group flex flex-col gap-3">
                  <b.icon className="h-6 w-6 text-gold transition-transform duration-500 group-hover:-translate-y-1" strokeWidth={1.75} />
                  <span className="font-mono text-[11px] font-semibold leading-relaxed tracking-[0.14em] text-slate-400">
                    {b.title}
                  </span>
                </div>
              ))}
            </motion.div>
          </div>
          <motion.div
            style={{ y: yDevices }}
            initial={{ opacity: 0, y: 64, rotate: 1.5 }}
            animate={{ opacity: 1, y: 0, rotate: 0 }}
            transition={{ duration: 1.2, delay: d + 0.55, ease: EASE }}
            className="lg:col-span-6"
          >
            <HeroDevices />
          </motion.div>
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: d + 1.4 }}
          className="mt-14 flex justify-center border-t border-navyline/50 pt-8"
        >
          <Link
            to="/#pricing"
            data-testid="hero-fast-path-link"
            className="group inline-flex items-center gap-3 font-mono text-[11px] font-semibold tracking-[0.22em] text-slate-500 transition-colors duration-300 hover:text-gold"
          >
            ALREADY KNOW WHAT YOU NEED?
            <span className="inline-flex items-center gap-1.5 text-gold">
              CHOOSE YOUR PACKAGE
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
            </span>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
